import { FC, useState, useMemo, useEffect, useCallback, useRef } from 'react';
import { Table } from '@/components/table';
import { ColumnDef } from '@tanstack/react-table';
import {
  Database,
  Table as TableIcon,
  Trash,
  Hourglass,
  RotateCcw,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  Sparkles,
  MoreVertical,
  Share2,
  Edit2,
  Copy,
} from 'lucide-react';
import {
  Collection,
  CollectionOrder,
  CollectionOrderType,
  CollectionSource,
} from '@/types/source-types';
import { useSearchParams } from 'react-router';
import { useOrgNavigate } from '@/hooks/use-org-navigate';
import { Button } from '@/components/ui/button';
import { SearchInput } from '@/components/ui/search-input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TruncateTextWithTooltip } from '@/components/ui/truncate-text-with-tooltip';
import { DeleteCollectionModal } from '@/components/delete-collection-modal';
import { ShareModal } from '@/components/share-modal';
import { EditCollectionModal } from './edit-collection-modal';
import { useOrgStore } from '@/stores/org-store';
import {
  deleteCollection,
  hardDeleteCollection,
} from '@/services/data-sources/delete-collection';
import { cancelCollectionDeletion } from '@/services/data-sources/cancel-collection-deletion';
import { useNotification } from '@/components/notifications';
import { ROUTES } from '@/constants/routes';
import { Pagination } from '@/components/pagination';
import { format } from 'date-fns';
import {
  extractWordPressConfig,
  inferCollectionSource,
  mapConfigToSitesState,
} from './wordpress-source-utils';
import { useGetSoftDeleteTtl } from '@/hooks/queries/data-sources/use-get-soft-delete-ttl';

interface CollectionsTableProps {
  collections: Collection[];
  isLoading: boolean;
  onCollectionSoftDeleted?: (collectionId: string) => void;
  onCollectionHardDeleted?: (collectionId: string) => void;
  handleNewCollectionModalOpen: () => void;
  onCollectionCancelDeletion?: (collectionId: string) => void;
  onCollectionUpdated?: (updatedCollection: Collection | null) => void;
}

const ITEMS_PER_PAGE_OPTIONS = [5, 10, 20, 50, 100];
const DEFAULT_ITEMS_PER_PAGE = 20;

const getCollectionSourceLabel = (source?: CollectionSource) =>
  source === 'wordpress_api' ? 'WordPress API' : 'Uploads';

export const CollectionsTable: FC<CollectionsTableProps> = ({
  collections,
  isLoading,
  onCollectionSoftDeleted,
  onCollectionHardDeleted,
  handleNewCollectionModalOpen,
  onCollectionCancelDeletion,
  onCollectionUpdated,
}) => {
  const navigate = useOrgNavigate();
  const notification = useNotification();
  const { currentOrg } = useOrgStore();
  const [searchParams, setSearchParams] = useSearchParams();
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] =
    useState<Collection | null>(null);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isCancelLoading, setIsCancelLoading] = useState(false);
  const [collectionToDelete, setCollectionToDelete] = useState<string>('');
  const [collectionToCancel, setCollectionToCancel] = useState<string>('');
  const [searchQuery, setSearchQuery] = useState('');
  const [orderBy, setOrderBy] = useState<CollectionOrderType>(
    CollectionOrder.CREATED_DESC,
  );
  const prevSearchQueryRef = useRef(searchQuery);

  // Fetch TTL configuration for soft delete
  const { data: ttlConfig } = useGetSoftDeleteTtl();
  const ttlValue = ttlConfig?.value ?? 7;
  const ttlUnit = ttlConfig?.unit ?? 'days';

  // Read page and per-page from URL, with defaults
  const pageFromUrl = parseInt(searchParams.get('page') || '1', 10);
  const perPageFromUrl = parseInt(
    searchParams.get('per-page') || String(DEFAULT_ITEMS_PER_PAGE),
    10,
  );

  // Validate and use URL params
  const currentPage = pageFromUrl > 0 ? pageFromUrl : 1;
  // Allow any positive number for per-page, not just predefined options
  const itemsPerPage =
    perPageFromUrl > 0 ? perPageFromUrl : DEFAULT_ITEMS_PER_PAGE;

  // Filter and sort collections
  const filteredCollections = useMemo(() => {
    let filtered = collections;

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = collections.filter(
        (collection) =>
          collection.name.toLowerCase().includes(query) ||
          collection.description?.toLowerCase().includes(query),
      );
    }

    // Apply sorting
    const sorted = [...filtered].sort((a, b) => {
      switch (orderBy) {
        case CollectionOrder.NAME_ASC:
          return a.name.localeCompare(b.name);
        case CollectionOrder.NAME_DESC:
          return b.name.localeCompare(a.name);
        case CollectionOrder.CREATED_ASC:
          return (
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
        case CollectionOrder.CREATED_DESC:
          return (
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
          );
        default:
          return 0;
      }
    });

    return sorted;
  }, [collections, searchQuery, orderBy]);

  // Calculate pagination based on filtered results
  const totalPages = useMemo(
    () => Math.ceil(filteredCollections.length / itemsPerPage),
    [filteredCollections.length, itemsPerPage],
  );
  const paginatedCollections = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredCollections.slice(startIndex, endIndex);
  }, [filteredCollections, currentPage, itemsPerPage]);

  // Handle page change
  const handlePageChange = useCallback(
    (page: number) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('page', String(page));
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  // Handle per-page change
  const handlePerPageChange = useCallback(
    (value: string) => {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('per-page', value);
      newParams.set('page', '1'); // Reset to page 1 when changing per-page
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  // Reset to first page when search query changes
  useEffect(() => {
    // Only reset if search query actually changed from previous value
    if (prevSearchQueryRef.current !== searchQuery && currentPage !== 1) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('page', '1');
      setSearchParams(newParams, { replace: true });
    }
    prevSearchQueryRef.current = searchQuery;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchQuery]);

  // Ensure current page is valid when total pages changes
  useEffect(() => {
    if (currentPage > totalPages && totalPages > 0) {
      handlePageChange(totalPages);
    }
  }, [currentPage, totalPages, handlePageChange]);

  const handleSoftDeleteCollection = async () => {
    if (!collectionToDelete) return;

    setIsDeleteLoading(true);
    try {
      const response = await deleteCollection(collectionToDelete);

      if (response.detail) {
        notification.error(response.detail);
        return;
      }

      notification.success('Collection scheduled for deletion successfully');
      onCollectionSoftDeleted?.(collectionToDelete);

      // If we're on the last page and it becomes empty, go to previous page
      const remainingItems = filteredCollections.length - 1;
      const newTotalPages = Math.ceil(remainingItems / itemsPerPage);
      if (currentPage > newTotalPages && newTotalPages > 0) {
        handlePageChange(newTotalPages);
      }
    } catch (err) {
      console.error('Failed to delete collection:', err);
      notification.error('Failed to delete collection.');
    } finally {
      setIsDeleteLoading(false);
      setIsDeleteModalOpen(false);
      setCollectionToDelete('');
    }
  };

  const handleHardDeleteCollection = async () => {
    if (!collectionToDelete) return;

    setIsDeleteLoading(true);
    try {
      const response = await hardDeleteCollection(collectionToDelete);

      if (response.detail) {
        notification.error(response.detail);
        return;
      }

      notification.success('Collection deleted immediately');
      onCollectionHardDeleted?.(collectionToDelete);

      // If we're on the last page and it becomes empty, go to previous page
      const remainingItems = filteredCollections.length - 1;
      const newTotalPages = Math.ceil(remainingItems / itemsPerPage);
      if (currentPage > newTotalPages && newTotalPages > 0) {
        handlePageChange(newTotalPages);
      }
    } catch (err) {
      console.error('Failed to hard delete collection:', err);
      notification.error('Failed to immediately delete collection.');
    } finally {
      setIsDeleteLoading(false);
      setIsDeleteModalOpen(false);
      setCollectionToDelete('');
    }
  };

  const handleCancelDeletion = useCallback(
    async (id: string) => {
      const collection = collections.find((c) => c.id === id);

      if (!collection) return;

      setIsCancelLoading(true);
      setCollectionToCancel(id);
      try {
        const response = await cancelCollectionDeletion({
          collectionId: collection.id,
        });

        if (response.detail) {
          notification.error(response.detail);
          return;
        }

        notification.success('Collection deletion cancelled successfully');

        // Notify parent component of cancellation
        onCollectionCancelDeletion?.(id);
      } catch (err) {
        console.error('Failed to cancel collection deletion:', err);
        notification.error('Failed to cancel collection deletion.');
      } finally {
        setIsCancelLoading(false);
        setCollectionToCancel('');
      }
    },
    [collections, notification, onCollectionCancelDeletion],
  );

  // Handle sorting
  const handleSort = useCallback(
    (column: string) => {
      const newOrderBy = (() => {
        if (column === 'name') {
          return orderBy === CollectionOrder.NAME_ASC
            ? CollectionOrder.NAME_DESC
            : CollectionOrder.NAME_ASC;
        }
        if (column === 'created_at') {
          return orderBy === CollectionOrder.CREATED_ASC
            ? CollectionOrder.CREATED_DESC
            : CollectionOrder.CREATED_ASC;
        }
        return orderBy;
      })();

      setOrderBy(newOrderBy);
    },
    [orderBy],
  );

  // Render sort icon
  const renderSortIcon = useCallback(
    (
      currentOrder: CollectionOrderType,
      ascOrder: CollectionOrder,
      descOrder: CollectionOrder,
    ) => {
      if (currentOrder === ascOrder) {
        return <ChevronUp className='w-4 h-4' />;
      }
      if (currentOrder === descOrder) {
        return <ChevronDown className='w-4 h-4' />;
      }
      return <ChevronsUpDown className='w-4 h-4' />;
    },
    [],
  );

  // Helper function to convert TTL to milliseconds
  const getTtlInMilliseconds = useCallback(
    (value: number, unit: string): number => {
      const unitMultipliers: Record<string, number> = {
        minutes: 60 * 1000,
        hours: 60 * 60 * 1000,
        days: 24 * 60 * 60 * 1000,
      };
      return value * (unitMultipliers[unit] || unitMultipliers.days);
    },
    [],
  );

  // Helper function to format time unit for display
  const formatTimeUnit = useCallback((count: number, unit: string): string => {
    // If count is 1 and unit is plural, make it singular
    if (count === 1 && unit.endsWith('s')) {
      return unit.slice(0, -1);
    }
    return unit;
  }, []);

  const columns: ColumnDef<Collection, unknown>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: () => (
          <button
            type='button'
            className='flex items-center gap-1'
            onClick={() => handleSort('name')}
          >
            Name
            {renderSortIcon(
              orderBy,
              CollectionOrder.NAME_ASC,
              CollectionOrder.NAME_DESC,
            )}
          </button>
        ),
        cell: ({ row }) => {
          const name = row.original.name;
          const collection = row.original;

          // Deletion icon logic
          const deletionIcon = (() => {
            if (
              collection.status === 'pending_deletion' &&
              collection.deletion_requested_at
            ) {
              const deletionDate = new Date(collection.deletion_requested_at);
              const ttlMilliseconds = getTtlInMilliseconds(ttlValue, ttlUnit);
              const expirationDate = new Date(
                deletionDate.getTime() + ttlMilliseconds,
              );
              const now = new Date();
              const timeLeftInMilliseconds =
                expirationDate.getTime() - now.getTime();
              const timeLeft = Math.ceil(
                timeLeftInMilliseconds / getTtlInMilliseconds(1, ttlUnit),
              );

              return (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Hourglass className='w-3 h-3' />
                  </TooltipTrigger>
                  <TooltipContent side='left'>
                    <div className='text-center'>
                      <div>
                        Deletion requested:{' '}
                        {format(new Date(deletionDate), 'MM/dd/yyyy')}
                      </div>
                      <div>
                        {timeLeft > 0
                          ? `${timeLeft} ${formatTimeUnit(timeLeft, ttlUnit)} remaining`
                          : 'Deletion overdue'}
                      </div>
                    </div>
                  </TooltipContent>
                </Tooltip>
              );
            }
            return null;
          })();

          return (
            <div className='flex flex-col gap-0.5'>
              <div className='flex items-center gap-2'>
                {deletionIcon}
                <span className='font-medium'>{name}</span>
              </div>
              <TruncateTextWithTooltip
                text={row.original.description || ''}
                className='text-sm text-muted-foreground leading-snug'
              />
            </div>
          );
        },
      },
      {
        accessorKey: 'owner_email',
        header: 'Owner',
        cell: ({ row }) => {
          const ownerEmail = row.original.owner_email;
          if (!ownerEmail) {
            return <span className='text-sm text-muted-foreground'>—</span>;
          }
          return <span className='text-sm'>{ownerEmail}</span>;
        },
      },
      {
        accessorKey: 'destination',
        header: 'Destination',
        cell: ({ row }) => {
          const destination = row.original.destination;
          const isStructured = row.original.data_type === 'structured';
          return (
            <div className='flex items-center gap-2'>
              {isStructured ? (
                <TableIcon className='w-4 h-4 text-gray-500 dark:text-gray-400' />
              ) : (
                <Database className='w-4 h-4 text-gray-500 dark:text-gray-400' />
              )}
              <span className='text-sm'>{destination}</span>
            </div>
          );
        },
      },
      {
        accessorKey: 'source',
        header: 'Source',
        cell: ({ row }) => {
          const source = inferCollectionSource(row.original);

          if (source === 'wordpress_api') {
            // Check for multi-site format first
            const sites = mapConfigToSitesState(row.original.source_config);

            if (sites.length > 1) {
              return (
                <div className='flex flex-col text-sm'>
                  <span className='font-medium'>WordPress API</span>
                  <span className='text-xs text-muted-foreground'>
                    {sites.length} sites
                  </span>
                  <span className='text-xs text-muted-foreground truncate max-w-[220px]'>
                    {sites.map((s) => s.label || s.baseUrl).join(', ')}
                  </span>
                </div>
              );
            }

            // Single site (new or legacy format)
            const configDetails =
              sites.length === 1
                ? {
                    baseUrl: sites[0].baseUrl,
                    scheduleCron: sites[0].scheduleCron,
                    hasCredentials: sites[0].hasCredentials,
                  }
                : extractWordPressConfig(row.original.source_config);

            return (
              <div className='flex flex-col text-sm'>
                <span className='font-medium'>WordPress API</span>
                <span className='text-xs text-muted-foreground truncate max-w-[220px]'>
                  {configDetails.baseUrl || 'Base URL not set'}
                </span>
                <span className='text-xs text-muted-foreground'>
                  {configDetails.scheduleCron || 'No cadence configured'}
                </span>
                <span className='text-xs text-muted-foreground'>
                  {configDetails.hasCredentials
                    ? 'Credentials stored'
                    : 'No credentials on file'}
                </span>
              </div>
            );
          }

          return (
            <span className='text-sm'>{getCollectionSourceLabel(source)}</span>
          );
        },
      },
      {
        accessorKey: 'created_at',
        header: () => (
          <button
            type='button'
            className='flex items-center gap-1'
            onClick={() => handleSort('created_at')}
          >
            Created At
            {renderSortIcon(
              orderBy,
              CollectionOrder.CREATED_ASC,
              CollectionOrder.CREATED_DESC,
            )}
          </button>
        ),
        cell: ({ row }) => {
          const createdAt = row.original.created_at;
          if (!createdAt) {
            return <span className='text-sm text-gray-500'>-</span>;
          }

          return (
            <span className='text-sm'>
              {format(new Date(createdAt), 'MM/dd/yyyy')}
            </span>
          );
        },
      },
      {
        id: 'chat',
        header: '',
        cell: ({ row }) => {
          const collection = row.original;
          const isUnstructuredQdrant =
            (collection.data_type || '').toLowerCase() === 'unstructured' &&
            (collection.destination || '').toLowerCase() === 'qdrant';
          const isStructured =
            (collection.data_type || '').toLowerCase() === 'structured';

          if (isStructured) {
            const datasets = collection.config?.datasets ?? [];
            const count = datasets.length;

            return (
              <div className='flex justify-start'>
                <Button
                  variant='ghost'
                  size='icon'
                  className='relative rounded-full bg-muted hover:bg-muted/80'
                  disabled={count === 0}
                  onClick={(event) => {
                    event.stopPropagation();
                    navigate(
                      `${ROUTES.DATA_SOURCES.ROOT}/${collection.id}/datasets`,
                    );
                  }}
                  aria-label={`${count} ${count === 1 ? 'dataset' : 'datasets'}`}
                  title={`${count} ${count === 1 ? 'dataset' : 'datasets'}`}
                >
                  <Database className='size-4 text-foreground' />
                  <span className='absolute -top-1 -right-1 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-[10px] font-semibold text-primary-foreground'>
                    {count}
                  </span>
                </Button>
              </div>
            );
          }

          if (!isUnstructuredQdrant) {
            return null;
          }

          return (
            <div className='flex justify-start'>
              <Button
                variant='outline'
                size='sm'
                onClick={(event) => {
                  event.stopPropagation();
                  navigate(
                    `${ROUTES.DATA_SOURCES.ROOT}/${collection.id}?chat=1`,
                  );
                }}
              >
                <Sparkles className='w-4 h-4 mr-2' />
                Chat
              </Button>
            </div>
          );
        },
        enableSorting: false,
      },
      {
        id: 'actions',
        header: '',
        cell: ({ row }) => {
          const collection = row.original;
          const isPendingDeletion = collection.status === 'pending_deletion';
          const isCurrentlyLoading =
            isCancelLoading && collectionToCancel === collection.id;
          const role = collection.user_role;
          const canEdit = role === 'owner' || role === 'editor';
          const canShare = role === 'owner';
          const canDelete = role === 'owner';

          return (
            <div className='flex justify-end'>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant='ghost'
                    size='icon'
                    onClick={(e) => e.stopPropagation()}
                  >
                    <MoreVertical className='size-4' />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align='end'>
                  {isPendingDeletion ? (
                    canDelete && (
                      <DropdownMenuItem
                        disabled={isCurrentlyLoading}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCancelDeletion(collection.id);
                        }}
                      >
                        <RotateCcw className='size-4 mr-2' />
                        Cancel deletion
                      </DropdownMenuItem>
                    )
                  ) : (
                    <>
                      {canShare && (
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCollection(collection);
                            setIsShareModalOpen(true);
                          }}
                        >
                          <Share2 className='size-4 mr-2' />
                          Share
                        </DropdownMenuItem>
                      )}
                      {canEdit && (
                        <DropdownMenuItem
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedCollection(collection);
                            setIsEditModalOpen(true);
                          }}
                        >
                          <Edit2 className='size-4 mr-2' />
                          Edit
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem
                        onClick={(e) => {
                          e.stopPropagation();
                          navigator.clipboard.writeText(collection.id);
                          notification.success('Collection ID copied');
                        }}
                      >
                        <Copy className='size-4 mr-2' />
                        Copy ID
                      </DropdownMenuItem>
                      {canDelete && (
                        <>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem
                            onClick={(e) => {
                              e.stopPropagation();
                              setCollectionToDelete(collection.id);
                              setIsDeleteModalOpen(true);
                            }}
                          >
                            <Trash className='size-4 mr-2' />
                            Delete
                          </DropdownMenuItem>
                        </>
                      )}
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          );
        },
        enableSorting: false,
      },
    ],
    [
      isCancelLoading,
      collectionToCancel,
      handleCancelDeletion,
      notification,
      orderBy,
      handleSort,
      renderSortIcon,
      navigate,
      ttlValue,
      ttlUnit,
      getTtlInMilliseconds,
      formatTimeUnit,
    ],
  );

  const handleRowClick = (collection: Collection) => {
    navigate(`${ROUTES.DATA_SOURCES.ROOT}/${collection.id}`);
  };

  return (
    <>
      {/* Search Input */}
      <div className='mb-4'>
        <div className='flex items-center justify-between'>
          <div className='w-96'>
            <SearchInput
              value={searchQuery}
              onChange={setSearchQuery}
              placeholder='Search collections by name or description...'
              aria-label='Search collections'
            />
          </div>
          <Button onClick={handleNewCollectionModalOpen}>
            Create collection
          </Button>
        </div>
        {searchQuery.trim() && (
          <p className='text-sm text-gray-600 mt-2'>
            Showing {filteredCollections.length} of {collections.length}{' '}
            collections
          </p>
        )}
      </div>

      <Table
        columns={columns}
        data={paginatedCollections}
        loading={isLoading}
        onRowClick={handleRowClick}
      />

      {filteredCollections.length > 0 && (
        <div className='mt-4 flex items-center justify-between'>
          {/* Left: Result count */}
          <div className='text-sm text-muted-foreground'>
            Showing {(currentPage - 1) * itemsPerPage + 1}-
            {Math.min(currentPage * itemsPerPage, filteredCollections.length)}{' '}
            of {filteredCollections.length}
          </div>

          {/* Center: Pagination */}
          <div className='flex-1 flex justify-center'>
            {totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </div>

          {/* Right: Per-page selector */}
          <div className='flex items-center gap-2 whitespace-nowrap'>
            <span className='text-sm text-muted-foreground'>Show</span>
            <Select
              value={String(itemsPerPage)}
              onValueChange={handlePerPageChange}
            >
              <SelectTrigger className='h-8 w-[80px] justify-center'>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {/* Show current value if it's not in predefined options */}
                {!ITEMS_PER_PAGE_OPTIONS.includes(itemsPerPage) && (
                  <SelectItem value={String(itemsPerPage)}>
                    {itemsPerPage}
                  </SelectItem>
                )}
                {ITEMS_PER_PAGE_OPTIONS.map((option) => (
                  <SelectItem key={option} value={String(option)}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className='text-sm text-muted-foreground'>per page</span>
          </div>
        </div>
      )}

      <DeleteCollectionModal
        isOpen={isDeleteModalOpen}
        isLoading={isDeleteLoading}
        handleSoftDelete={handleSoftDeleteCollection}
        handleHardDelete={handleHardDeleteCollection}
        handleCancel={() => {
          setIsDeleteModalOpen(false);
          setCollectionToDelete('');
        }}
      />

      <ShareModal
        resourceType='collection'
        resourceId={selectedCollection?.id ?? ''}
        orgId={currentOrg?.orgid ?? null}
        open={isShareModalOpen}
        onOpenChange={(open) => {
          setIsShareModalOpen(open);
          if (!open) setSelectedCollection(null);
        }}
      />

      {selectedCollection && (
        <EditCollectionModal
          isOpen={isEditModalOpen}
          onOpenChange={(open) => {
            setIsEditModalOpen(open);
            if (!open) setSelectedCollection(null);
          }}
          collection={selectedCollection}
          collectionId={selectedCollection.id}
          collections={collections}
          onCollectionUpdated={(updated) => {
            onCollectionUpdated?.(updated);
            setIsEditModalOpen(false);
            setSelectedCollection(null);
          }}
        />
      )}
    </>
  );
};
